import moment from 'moment';
import ShelterlyPDF from '../utils/pdf';
import { capitalize } from '../utils/formatString';
import { priorityChoices, priorityText, DATE_FORMAT } from '../constants';
import { statusChoicesNFA } from '../animals/constants';
import { buildAnimalCountList } from '../animals/Utils';

function buildNoteList(pdf, notes, {
  countLabelMarginTo = 0
} = {}) {
  pdf.drawTextList({
    labels: notes.map((note) => (
      // capitalize the species
      `${'Created'}: ${moment(note.open).format('MMMM Do YYYY HH:mm')}`,
      `${'Author'}: ${note.author_name}`,
      `${moment(note.open).format(
              'MMMM Do'
            )}: ${note.notes}`
    )),
    labelMarginTop: countLabelMarginTo
  });
}

function buildDispatchSummaries(pdf, drs) {
  drs.forEach((data, i) => {
    // Team section
    pdf.drawSectionHeader({
      text: `FA#${data.id_for_incident} - Team: ${data.team_name || 'N/A'}`,
      hRule: false,
      fontSize: 14
    });
    pdf.drawPad(4);

    pdf.drawSectionHeader({
      text: 'Members:',
      hRule: false,
      fontSize: 12
    });
    pdf.drawPad(4);

    data.team_members_with_contact.forEach((member) => {
      pdf.drawSectionHeader({
        text: member,
        hRule: false,
        fontSize: 10
      });
      pdf.drawPad(1);
    });

    // Assigned Requests
    data.assigned_requests.forEach((assigned_request) => {
      const sro = assigned_request.service_request_object;
      const srPriority = priorityText[assigned_request.service_request_object.priority]
      pdf.drawPad(8);
      pdf.drawSectionHeader({
        text: `SR#${sro.id_for_incident} - ${sro.full_address}`,
        fontSize: 12
      });
      pdf.drawPad(4)
      if (sro.priority === 1) {
        pdf.setDocumentColors({ rgb: [255, 76, 76] }); // red
      }
      pdf.drawSectionHeader({
        text: `${srPriority} Priority`,
        hRule: false,
        fontSize: 10
      })
      pdf.setDocumentColors({ rgb: [0, 0, 0] }); // reset to black or default
      pdf.drawPad(4);
      pdf.drawSectionHeader({
        text: `Followup Date: ${sro.followup_date}`,
        hRule: false,
        fontSize: 10 
      })
      pdf.drawPad(4);
      pdf.drawSectionHeader({
        text: `Lat: ${sro.latitude}, Long: ${sro.longitude}`,
        hRule: false,
        fontSize: 10
      });
      pdf.drawPad(4);

      const counts = sro.status_species_counts || {};
      Object.entries(counts).forEach(([status, speciesCounts]) => {
        if (Object.keys(speciesCounts).length === 0) return;

        const speciesText = Object.entries(speciesCounts)
          .map(([species, count]) => `${count} ${species}`)
          .join(', ');

        pdf.drawSectionHeader({
          text: `${status}: ${speciesText}`,
          hRule: false,
          fontSize: 10
        });
      });
      pdf.drawPad(4);
    });
    pdf.drawHRule();
  });
}

const buildDispatchSummaryDoc = (data={}) => {
  const pdf = new ShelterlyPDF({}, {
    addFooterHandler: ShelterlyPDF.HandlerTypes.DEFAULT,
    pageTitle: 'Field Assignment Summary',
    pageSubtitle: data.drs.length
      ? `Organization: ${data.organization} | Incident: ${data.incident} | Date: ${new Date().toLocaleDateString()}`
      : ''
  });
  pdf.drawPageHeader({
    pageTitle: `Field Assignment Summary`,
  });
  buildDispatchSummaries(pdf, data.drs);
  return pdf;
};



const buildDispatchResolutionsDoc = (data={}) => {
  const pdf = new ShelterlyPDF({}, {
    // adds page numbers to the footer
    addFooterHandler: ShelterlyPDF.HandlerTypes.DEFAULT,
    pageTitle: `Field Assignment ${data.drs.length ? `#${data.drs[0].id_for_incident}` : ''}`,
    pageSubtitle: data.drs.length
      ? `Organization: ${data.organization} | Incident: ${data.incident} | Date: ${new Date().toLocaleDateString()}`
      : ''
  });

  buildDispatchSummaries(pdf, data.drs)
  pdf.drawPageBreak();
  data.drs.forEach((data, i) => {
    // loop through SR's and page break between each one
    data.assigned_requests.forEach((assigned_request, index) => {

      const srPriority = priorityText[assigned_request.service_request_object.priority]
      if (index > 0) {
        pdf.drawPageBreak();
      }
      pdf.drawHRect({ color: 'GREY', height: 100 });
      // SR Header
      pdf.drawSectionHeader({
        text: `SR#${assigned_request.service_request_object.id_for_incident} | COMPLETED | UNABLE TO COMPLETE`,
        hRule: false,
        fontSize: 14
      });
      pdf.drawPad(10);
      if (assigned_request.service_request_object.priority === 1) {
        pdf.setDocumentColors({ rgb: [255, 76, 76] }); // red
      }

      pdf.drawSectionHeader({
        text: `${srPriority} Priority`,
        hRule: false,
        fontSize:14
      })
      pdf.setDocumentColors({ rgb: [0, 0, 0] });
      pdf.drawPad(10)
      pdf.drawSectionHeader({
        text: `${assigned_request.service_request_object.full_address}`,
        hRule: false,
        fontSize: 14,
      });
      pdf.drawPad(8);
      pdf.drawSectionHeader({
        text: `Followup Date: ${assigned_request.service_request_object.followup_date}`,
        hRule: false,
        fontSize: 14,
      })
      pdf.drawPad(15);
      pdf.drawWrappedText({
        text: `Latitude: ${assigned_request.service_request_object.latitude},  Longitude: ${assigned_request.service_request_object.longitude}`
      });
      pdf.drawTextList({
        labels: [
          `Accessible: ${assigned_request.service_request_object.accessible ? 'Yes' : 'No'}`,
          `Verbal Liability Release: ${assigned_request.service_request_object.verbal_permission ? 'Yes' : 'No'}`,
          `Turn Around: ${assigned_request.service_request_object.turn_around ? 'Yes' : 'No'}`,
          `Key at Staging: ${assigned_request.service_request_object.key_provided ? 'Yes': 'No'}`
        ],
        listStyle: 'grid',
      });
      pdf.drawPad(15);
      // Generate Owner/Reporter Strings
      // owners
      if (assigned_request.service_request_object.owners.length) {
        assigned_request.service_request_object.owner_objects.forEach(owner => {
          const altPhone = owner.display_alt_phone ? ` / ${owner.display_alt_phone}` : "";
          pdf.drawWrappedText({
            text: `Owner: ${owner.first_name} ${owner.last_name} ${owner.display_phone}${altPhone}`
          });
          pdf.drawWrappedText({
            text: `Comments / Alternate Contact: ${owner.comments || "N/A"}`
          });
        });
      } else {
        pdf.drawWrappedText({ text: "Owner: N/A" });
        pdf.drawWrappedText({ text: "Comments / Alternate Contact: N/A" });
      }

      // reporter
      let reporter_string = '';
      if (assigned_request.service_request_object.reporter_object) {
          reporter_string = `Reporter: ${assigned_request.service_request_object.reporter_object.first_name} ${assigned_request.service_request_object.reporter_object.last_name} ${assigned_request.service_request_object.reporter_object.agency ? '(' + assigned_request.service_request_object.reporter_object.agency + ')' : 'No'} ${assigned_request.service_request_object.reporter_object.display_phone}`
      } else {
        reporter_string = 'Reporter: N/A';
      }
      pdf.drawWrappedText({text: reporter_string})
      let instructions_string = `Instructions for Field Team: ${assigned_request.service_request_object.directions || 'N/A'}`
      pdf.drawWrappedText({text: instructions_string});
      pdf.drawHRule();

      const activeAnimals = Object.values(assigned_request.animals).filter(
        (animal) => animal.status !== "CANCELED"
      );
      activeAnimals.forEach((animal) => {
        // if very little page is left that would cause a weird break between the header, manually page break now
        const estimatedReleaseSectionHeight = 106;
        if (pdf.remainderPageHeight <= estimatedReleaseSectionHeight) {
          pdf.drawPageBreak();
        }
        // draw the animals header in each row
        pdf.setDocumentFontSize({ size: 11 });
        if (animal.animal_count > 1) {
          pdf.drawSectionHeader({text: `A#${animal.id_for_incident} ${animal.animal_count} ${capitalize(animal.species)} - ${capitalize(animal.status)}`, hRule: false, size: 12})
        }
        else {
            pdf.drawSectionHeader({text: `A#${animal.id_for_incident} ${capitalize(animal.species)} "${capitalize(animal.name) || "Unknown Name"}"  - ${capitalize(animal.status)}`, hRule: false, size: 12})
        }

        pdf.drawTextList({
          labels: ['Result: SIP | SHELTERED | UTL | NFA | REUNITED | CARE TRANSFERRED | DECEASED | DNSF'],
          listStyle: 'inline',
          size: 10
        })

        pdf.setDocumentFontSize({ size: 10 });

        pdf.drawTextList({
          labels: [
            `Sex: ${animal.sex || 'N/A'}`,
            `Special Handling Required: ${animal.aco_required ? capitalize(animal.aco_required) : 'N/A'}`,
            `Fixed: ${animal.fixed ? capitalize(animal.fixed) : 'N/A'}`,
            `Confined: ${animal.confined ? capitalize(animal.confined) : 'N/A'}`,
            `Aggressive: ${animal.aggressive ? capitalize(animal.aggressive) : 'N/A'}`,
            `Injured: ${animal.injured ? capitalize(animal.injured) : 'N/A'}`,
            `Size: ${animal.size ? capitalize(animal.size) : 'N/A'}`,
            `Age: ${animal.age ? capitalize(animal.age) : 'N/A'}`,
            `Primary Color: ${animal.pcolor ? capitalize(animal.pcolor) : 'N/A'}`,
            `Secondary Color: ${animal.scolor ? capitalize(animal.scolor) : 'N/A'}`,
          ],
          listStyle: 'grid',
          labelMarginTop: -4
        });

        pdf.drawSectionHeader({
          text: `Description: ${animal.color_notes || 'N/A'}`,
          fontSize: 10
        });
        pdf.drawPad(6);
        pdf.drawSectionHeader({
          text: `Animal Notes: ${animal.behavior_notes ? animal.behavior_notes : animal.animal_notes || 'N/A'}`,
          fontSize: 10
        });
        pdf.drawPad(6);
        pdf.drawSectionHeader({
          text: `Medical Notes: ${animal.medical_notes || 'N/A'}`,
          fontSize: 10
        });
        pdf.drawPad(10);
        pdf.drawHRule();
      });


      // priorities
      const currentPriority = assigned_request.service_request_object.priority || 2;
      const prioritiesString = priorityChoices
        .map(({ value, label }) => {
          return value === currentPriority ? `${label} (Current)` : label;
        })
        .join(' | ');
      pdf.drawWrappedText({text: 'Priority: ' + prioritiesString, bottomPadding: -5});

      // date completed, followup, etc..
      pdf.drawTextList({
        labels: [
          'Date Completed:  ________/________/________________'
        ],
        bottomPadding: 5
      });
      pdf.drawTextList({
        labels: [
          'Followup Date:  ________/________/________________'
        ],
        bottomPadding: 5
      })

      const labels = ['Forced Entry Used'];
      if (assigned_request.service_request_object.owners.length) {
        labels.push('Owner Notified of Outcome');
      }
      pdf.drawCheckboxList({
         labels: labels,
         listStyle: 'inline',
       });
      // Check if there is enough room on that page for the Visit Notes section.
      const estimatedVisitNotesSectionHeight = 170;
      if (pdf.remainderPageHeight <= estimatedVisitNotesSectionHeight) {
        pdf.drawPageBreak();
      }
      pdf.drawWrappedText({
        text: `Visit Notes:`,
        fonsSize: 11
      });
      pdf.drawHRect({color:'GREY', style:'S', height:150})
      pdf.drawPad(160);

      if (assigned_request.service_request_object.visit_notes.filter(vn => vn.date_completed < assigned_request.visit_note ? assigned_request.visit_note.date_completed : null).length > 0) {
        pdf.drawWrappedText({
          text: `Previous Visit Notes`,
          fontSize: 12
        });
        assigned_request.service_request_object.visit_notes.filter(vn => vn.date_completed < assigned_request.visit_note.date_completed).forEach((visit_note) => {
          pdf.drawWrappedText({
            text: `${moment(visit_note.date_completed).format(
              'MMMM Do'
            )}: ${(visit_note?.notes && visit_note?.notes) || 'No information available.'}`,
            fontSize: 11
          });
        })
      }

      if (assigned_request.service_request_object.notes.filter(note => note.urgent === true).length > 0) {
        pdf.drawSectionHeader({ text: 'Shared Service Request Notes', fontSize: 12 });
        pdf.setDocumentFontSize({ size: 11 });
        buildNoteList(pdf, assigned_request.service_request_object.notes.filter(note => note.urgent === true), { countLabelMarginTop: -12 });
        // reset document font size
        pdf.setDocumentFontSize();
      }
    });
  });
  return pdf;
}

function printDispatchResolutionForm(data = {}) {
  const pdf = buildDispatchResolutionsDoc(data);
  pdf.fileName = `FA-${data.drs[0].id_for_incident.toString().padStart(3, 0)}`;
  return pdf.saveFile();
}

function printAllDispatchResolutions(data = {}) {
  const pdf = buildDispatchResolutionsDoc(data);
  pdf.fileName = `FAs-${moment().format(DATE_FORMAT)}`;
  return pdf.saveFile();
}

function printDispatchSummary(data = {}) {
  const pdf = buildDispatchSummaryDoc(data);
  pdf.fileName = `FAs-Summary-${moment().format(DATE_FORMAT)}`;
  return pdf.saveFile();
}

export {
  printDispatchResolutionForm,
  printAllDispatchResolutions,
  printDispatchSummary
};
